/**
 * 基础HTTP请求模块
 *
 * @description 提供简洁的HTTP请求函数，遵循KISS原则
 * @author Cross-Border ERP Team
 * @version 1.0.0
 */

import {request} from '@umijs/max';
import {ApiPaginatedResponse, ApiPaginationParams, ApiRequestConfig, ApiResponse} from "@/services/typings";
import { getAuthToken } from '@/utils/auth';

/**
 * 检查URL是否需要认证
 *
 * @param url 请求URL
 * @returns 是否需要认证
 */
function needsAuthentication(url: string): boolean {
  // 不需要认证的URL列表
  const publicUrls = [
    '/api/auth/login',
    '/api/auth/register', 
    '/api/health',
    '/api/ping',
    '/api/auth/password/email',
    '/api/auth/password/reset',
  ];
  
  return !publicUrls.some(publicUrl => url.includes(publicUrl));
}

/**
 * 构建统一的请求头
 *
 * @param customHeaders 自定义请求头
 * @param url 请求URL（用于判断是否需要认证）
 * @returns 包含统一请求头的对象
 */
function buildRequestHeaders(customHeaders: Record<string, string> = {}, url: string = ''): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-API-Version': 'v1',
    'Accept': 'application/json',
  };

  // 添加认证token（如果存在且需要认证）
  if (needsAuthentication(url)) {
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  // 合并自定义头部，自定义头部优先级更高
  return { ...headers, ...customHeaders };
}

/**
 * 处理请求配置，包括方法伪造和请求头设置
 *
 * @param config 原始请求配置
 * @returns 处理后的请求配置
 */
function processRequestConfig(config: ApiRequestConfig): {
  method: string;
  headers: Record<string, string>;
  data: any;
} {
  const { headers = {}, multipart = false, method, data, url } = config;
  
  // 构建基础请求头
  const processedHeaders = buildRequestHeaders(headers, url);
  let processedMethod = method.toUpperCase();
  let processedData = data;

  // Laravel 表单方法伪造：当 PUT/PATCH + multipart 时，使用 POST + _method
  const needsMethodSpoofing = multipart && ['PUT', 'PATCH'].includes(processedMethod);

  if (needsMethodSpoofing) {
    // 将方法改为 POST
    processedMethod = 'POST';

    // 在 FormData 中添加 _method 字段
    if (processedData instanceof FormData) {
      processedData.append('_method', method.toUpperCase());
    } else {
      // 如果不是 FormData，创建新的 FormData
      const formData = new FormData();
      formData.append('_method', method.toUpperCase());

      // 如果有其他数据，尝试添加到 FormData
      if (processedData && typeof processedData === 'object') {
        Object.entries(processedData).forEach(([key, value]) => {
          if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, String(value));
          }
        });
      }
      processedData = formData;
    }
  }

  // 处理 Content-Type（对于 multipart 请求需要特殊处理）
  if (multipart || needsMethodSpoofing) {
    // 对于 multipart/form-data，删除 Content-Type
    // 让浏览器自动设置正确的 boundary
    delete processedHeaders['Content-Type'];
  }

  return {
    method: processedMethod,
    headers: processedHeaders,
    data: processedData,
  };
}

/**
 * 执行HTTP请求的核心函数
 *
 * @param config 请求配置
 * @returns Promise<ApiResponse<T>>
 */
async function makeRequest<T>(config: ApiRequestConfig): Promise<ApiResponse<T>> {
  try {
    // 处理请求配置（包括方法伪造）
    const { method, headers, data } = processRequestConfig(config);

    return await request<ApiResponse<T>>(config.url, {
      method,
      params: config.params,
      data,
      headers,
      timeout: config.timeout || 30000,
      skipErrorHandler: config.skipErrorHandler,
    });
  } catch (error) {
    // 错误处理依赖现有的 requestErrorConfig.ts
    throw error;
  }
}

/**
 * GET请求
 *
 * @description 发送GET请求获取数据
 * @template T 响应数据类型
 * @param url 请求URL
 * @param params 查询参数
 * @param config 额外配置
 * @returns Promise<ApiResponse<T>>
 *
 * @example
 * ```typescript
 * // 获取用户列表
 * const users = await get<User[]>('/api/users');
 *
 * // 带参数查询
 * const result = await get<User[]>('/api/users', { page: 1, size: 10 });
 * ```
 */
export async function get<T = any>(
  url: string,
  params?: Record<string, any>,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  return makeRequest<T>({
    url,
    method: 'GET',
    params,
    ...config,
  });
}

/**
 * POST请求
 *
 * @description 发送POST请求创建或提交数据，默认使用 application/json Content-Type
 * @template T 响应数据类型
 * @param url 请求URL
 * @param data 请求体数据
 * @param config 额外配置
 * @returns Promise<ApiResponse<T>>
 *
 * @example
 * ```typescript
 * // 创建用户（默认 JSON）
 * const user = await post<User>('/api/users', { name: 'John', email: 'john@example.com' });
 *
 * // 文件上传（超级简洁！）
 * const result = await post<any>('/api/upload', formData, { multipart: true });
 *
 * // 自定义 Content-Type
 * const result = await post<any>('/api/custom', data, {
 *   headers: { 'Content-Type': 'application/xml' }
 * });
 * ```
 */
export async function post<T = any>(
  url: string,
  data?: any,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  return makeRequest<T>({
    url,
    method: 'POST',
    data,
    ...config,
  });
}

/**
 * PUT请求
 *
 * @description 发送PUT请求更新资源，默认使用 application/json Content-Type
 * @template T 响应数据类型
 * @param url 请求URL
 * @param data 请求体数据
 * @param config 额外配置
 * @returns Promise<ApiResponse<T>>
 *
 * @example
 * ```typescript
 * // 更新用户信息（默认 JSON）
 * const user = await put<User>('/api/users/1', { name: 'John Updated' });
 *
 * // 文件上传更新（自动使用 POST + _method=PUT）
 * const result = await put<any>('/api/users/1/avatar', formData, { multipart: true });
 * ```
 */
export async function put<T = any>(
  url: string,
  data?: any,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  return makeRequest<T>({
    url,
    method: 'PUT',
    data,
    ...config,
  });
}

/**
 * PATCH请求
 *
 * @description 发送PATCH请求部分更新资源，默认使用 application/json Content-Type
 * @template T 响应数据类型
 * @param url 请求URL
 * @param data 请求体数据
 * @param config 额外配置
 * @returns Promise<ApiResponse<T>>
 *
 * @example
 * ```typescript
 * // 部分更新用户（默认 JSON）
 * const user = await patch<User>('/api/users/1', { email: 'new@example.com' });
 *
 * // 部分更新包含文件（自动使用 POST + _method=PATCH）
 * const result = await patch<any>('/api/users/1', formData, { multipart: true });
 * ```
 */
export async function patch<T = any>(
  url: string,
  data?: any,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  return makeRequest<T>({
    url,
    method: 'PATCH',
    data,
    ...config,
  });
}

/**
 * DELETE请求
 *
 * @description 发送DELETE请求删除资源
 * @template T 响应数据类型
 * @param url 请求URL
 * @param config 额外配置
 * @returns Promise<ApiResponse<T>>
 *
 * @example
 * ```typescript
 * // 删除用户
 * const result = await del<any>('/api/users/1');
 *
 * // 注意：使用 del 而不是 delete，因为 delete 是 JavaScript 关键字
 * ```
 */
export async function del<T = any>(
  url: string,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  return makeRequest<T>({
    url,
    method: 'DELETE',
    ...config,
  });
}

export async function getPaginated<T = any>(
  url: string,
  paginationParams?: ApiPaginationParams,
  extraParams?: Record<string, any>,
  config?: Partial<ApiRequestConfig>
): Promise<ApiPaginatedResponse<T>> {
  // 合并分页参数和额外参数
  const params = {
    ...paginationParams,
    ...extraParams,
  };

  // 使用基础的 get 函数发送请求
  // 类型断言为 PaginatedResponse，因为我们知道这是分页请求
  return get<{
    items: T[];
    pagination: any;
  }>(url, params, config) as Promise<ApiPaginatedResponse<T>>;
}

/**
 * 批量创建
 *
 * @description 批量创建多个资源
 * @template T 响应数据类型
 * @param items 要创建的数据数组
 * @param url 请求URL
 * @param config 额外的请求配置
 * @returns Promise<ApiResponse<T[]>>
 */
export async function batchCreate<T = any>(
  items: any[],
  url: string,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T[]>> {
  return post<T[]>(url, { items }, config);
}

/**
 * 批量更新
 *
 * @description 批量更新多个资源，每个项目必须包含id字段
 * @template T 响应数据类型
 * @param items 要更新的数据数组，每个项目必须包含id字段
 * @param url 请求URL
 * @param config 额外的请求配置
 * @returns Promise<ApiResponse<T[]>>
 */
export async function batchUpdate<T = any>(
  items: Array<{ id: number | string; [key: string]: any }>,
  url: string,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T[]>> {
  return put<T[]>(url, { items }, config);
}

/**
 * 批量删除
 *
 * @description 批量删除多个资源，传递ID数组
 * @template T 响应数据类型
 * @param ids 要删除的资源ID数组
 * @param url 请求URL
 * @param config 额外的请求配置
 * @returns Promise<ApiResponse<T>>
 */
export async function batchDel<T = any>(
  ids: Array<number | string>,
  url: string,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  return del<T>(url, {
    ...config,
    data: { ids },
  });
}

// ============= 文件上传便捷方法 =============

/**
 * 单文件上传
 *
 * @description 上传单个文件，自动处理 FormData 和 Content-Type
 * @template T 响应数据类型
 * @param url 上传URL
 * @param file 要上传的文件或已构建的 FormData
 * @param additionalData 额外的表单数据（可选）
 * @param config 额外配置
 * @returns Promise<ApiResponse<T>>
 *
 * @example
 * ```typescript
 * // 上传单个文件
 * const result = await uploadFile<{url: string}>('/api/upload', file);
 *
 * // 上传文件并附加额外数据
 * const result = await uploadFile('/api/upload', file, {
 *   category: 'avatar',
 *   userId: '123'
 * });
 *
 * // 使用已构建的 FormData
 * const formData = new FormData();
 * formData.append('file', file);
 * formData.append('category', 'document');
 * const result = await uploadFile('/api/upload', formData);
 * ```
 */
export async function uploadFile<T = any>(
  url: string,
  file: File | FormData,
  additionalData?: Record<string, string | number | boolean>,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  let formData: FormData;

  if (file instanceof FormData) {
    formData = file;
    // 如果提供了额外数据，添加到现有的 FormData 中
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }
  } else {
    // 创建新的 FormData
    formData = new FormData();
    formData.append('file', file);

    // 添加额外数据
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }
  }

  return post<T>(url, formData, {
    ...config,
    multipart: true,
  });
}

/**
 * 多文件上传
 *
 * @description 上传多个文件，自动处理 FormData 和 Content-Type
 * @template T 响应数据类型
 * @param url 上传URL
 * @param files 要上传的文件数组或已构建的 FormData
 * @param additionalData 额外的表单数据（可选）
 * @param config 额外配置
 * @returns Promise<ApiResponse<T>>
 *
 * @example
 * ```typescript
 * // 上传多个文件
 * const files = [file1, file2, file3];
 * const result = await uploadFiles<{urls: string[]}>('/api/upload-multiple', files);
 *
 * // 上传文件并附加额外数据
 * const result = await uploadFiles('/api/upload-multiple', files, {
 *   category: 'documents',
 *   userId: '123'
 * });
 * ```
 */
export async function uploadFiles<T = any>(
  url: string,
  files: File[] | FormData,
  additionalData?: Record<string, string | number | boolean>,
  config?: Partial<ApiRequestConfig>
): Promise<ApiResponse<T>> {
  let formData: FormData;

  if (files instanceof FormData) {
    formData = files;
    // 如果提供了额外数据，添加到现有的 FormData 中
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }
  } else {
    // 创建新的 FormData
    formData = new FormData();

    // 添加所有文件
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    // 添加额外数据
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }
  }

  return post<T>(url, formData, {
    ...config,
    multipart: true,
  });
}

